"use client";

import React from "react";
import { Sparkles, LayoutDashboard, Package, TrendingUp, ArrowRight, Mail, Quote, ArrowDown } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

/**
 * ScaleAndIntegrate — Für Agenturen und Freelancer
 *
 * Komplett-Redesign der vorherigen 4-Karten-Sektion:
 * - Money-Math als Hero-Element (Sie zahlen → verrechnen → Ihre Marge)
 * - Kompakte horizontale Feature-Karten (Mobile-optimiert)
 * - Stärkerer CTA-Block mit Sarah-Steiner-Testimonial
 *
 * ID: skalieren-integrieren (unverändert für Header-Nav-Anker)
 */

const features = [{
  icon: Sparkles,
  title: "KI-Builder für jeden Kunden",
  description: "Professionelle Kunden-Websites in Minuten statt Wochen — für jede Branche."
}, {
  icon: LayoutDashboard,
  title: "Ein Dashboard, alle Kunden",
  description: "Sämtliche Kundenprojekte zentral verwalten — erstellen, bearbeiten, publizieren."
}, {
  icon: Package,
  title: "5er, 20er, 50er-Pakete",
  description: "Je mehr Kunden, desto günstiger pro Website. Custom-Pakete auf Anfrage."
}, {
  icon: TrendingUp,
  title: "Skalieren ohne Limits",
  description: "Ob 3 oder 300 Kunden — Hosting, SSL und Updates für jede Website inklusive."
}];
export default function ScaleAndIntegrate() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: mathRef,
    isVisible: mathVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="skalieren-integrieren" className="bg-background py-20 md:py-24 px-4 md:px-8 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        {/* ── Header ── */}
        <div ref={headerRef} className={`max-w-3xl mb-12 md:mb-16 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium tracking-wide mb-5">
            <span className="w-2 h-2 rounded-full bg-primary" />
            Für Agenturen &amp; Freelancer
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 md:mb-5 tracking-tight leading-[1.1]">
            Skalieren Sie Ihr Web-Business —{" "}
            <span className="text-primary">mit Schweizer Marge.</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            Sie betreuen KMU-Kunden? Bauen Sie deren Websites über PAGEMAKER24 —
            schneller, günstiger und mit deutlich mehr Marge pro Kunde.
          </p>
        </div>

        {/* ── Money-Math Hero ── */}
        <div ref={mathRef} className={`relative mb-12 md:mb-16 transition-all duration-700 delay-100 ease-out ${mathVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="relative bg-gradient-to-br from-primary/5 via-primary/[0.03] to-transparent border border-primary/20 rounded-2xl md:rounded-3xl p-6 md:p-10 overflow-hidden">
            {/* Subtle dot pattern */}
            <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
            backgroundImage: "radial-gradient(circle, var(--color-foreground) 1px, transparent 1px)",
            backgroundSize: "20px 20px"
          }} />

            <div className="relative">
              <div className="text-center mb-8 md:mb-10">
                <div className="text-[11px] font-semibold tracking-[0.2em] text-muted-foreground uppercase mb-2">
                  Beispielrechnung · 20er-Paket
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                  Ihre Marge, schwarz auf weiss.
                </h3>
              </div>

              {/* 3-step money flow */}
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-4 md:gap-2 items-stretch">
                {/* Sie zahlen */}
                <div className="bg-background border border-border rounded-xl p-5 md:p-6 text-center">
                  <div className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase mb-2">
                    Sie zahlen
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-foreground mb-1 leading-none">
                    ~CHF 15
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                    pro Website / Monat
                  </div>
                </div>

                {/* Arrow connector */}
                <div className="flex items-center justify-center text-muted-foreground/40 py-2 md:py-0">
                  <ArrowRight className="w-5 h-5 hidden md:block" />
                  <ArrowDown className="w-5 h-5 md:hidden" />
                </div>

                {/* Sie verrechnen */}
                <div className="bg-background border border-border rounded-xl p-5 md:p-6 text-center">
                  <div className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase mb-2">
                    Sie verrechnen
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-foreground mb-1 leading-none">
                    CHF 50–100
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                    pro Website / Monat
                  </div>
                </div>

                {/* Arrow connector */}
                <div className="flex items-center justify-center text-muted-foreground/40 py-2 md:py-0">
                  <ArrowRight className="w-5 h-5 hidden md:block" />
                  <ArrowDown className="w-5 h-5 md:hidden" />
                </div>

                {/* Ihre Marge */}
                <div className="bg-primary text-white rounded-xl p-5 md:p-6 text-center shadow-lg shadow-primary/20 relative">
                  <div className="text-[10px] font-semibold tracking-wider text-white/80 uppercase mb-2">
                    Ihre Marge
                  </div>
                  <div className="text-3xl md:text-4xl font-bold mb-1 leading-none">
                    CHF 700–1'700
                  </div>
                  <div className="text-xs text-white/80 mt-2">
                    pro Monat · 20 Kunden
                  </div>
                </div>
              </div>

              <p className="text-center text-xs text-muted-foreground mt-6 md:mt-8">
                Skalieren Sie auf 50+ Kunden — wir bauen Ihnen ein individuelles Paket.
              </p>
            </div>
          </div>
        </div>

        {/* ── Compact horizontal feature cards ── */}
        <div ref={cardsRef} className={`grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-12 md:mb-16 transition-all duration-700 delay-200 ease-out ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {features.map((f, i) => <div key={i} className="flex items-start gap-4 bg-card border border-border rounded-xl p-4 md:p-5 hover:border-primary/40 hover:shadow-md transition-all duration-300">
              <div className="size-10 md:size-11 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center">
                <f.icon className="size-5 text-primary" strokeWidth={2} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-bold text-foreground mb-1 tracking-tight">
                  {f.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {f.description}
                </p>
              </div>
            </div>)}
        </div>

        {/* ── CTA Block ── */}
        <div ref={ctaRef} className={`grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 lg:gap-8 items-center bg-foreground text-background rounded-2xl p-6 md:p-8 transition-all duration-700 delay-300 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {/* Left: Testimonial */}
          <div className="flex items-start gap-4">
            <Quote className="w-8 h-8 md:w-10 md:h-10 text-primary shrink-0 rotate-180 opacity-60" strokeWidth={1.5} />
            <div>
              <p className="text-base md:text-lg leading-snug font-medium mb-2 italic">
                «Wir betreuen über 15 KMU-Kunden über PAGEMAKER24. Ein Dashboard,
                alles im Griff. Das spart uns enorm viel Zeit.»
              </p>
              <div className="text-sm text-background/70">
                <span className="font-semibold text-background">
                  Sarah Steiner
                </span>{" "}
                · Web-Agentur, Winterthur
              </div>
            </div>
          </div>

          {/* Right: CTA */}
          <div className="flex flex-col items-stretch lg:items-end gap-3">
            <a href="mailto:info@pagemaker24.ch?subject=AGENTUR%20%26%20Freelancer" className="group inline-flex items-center justify-center gap-2 rounded-full px-6 md:px-7 py-3.5 text-sm md:text-base font-semibold transition-all bg-primary text-white shadow-lg shadow-primary/30 hover:shadow-xl hover:scale-105 whitespace-nowrap">
              <Mail className="size-4 md:size-5" />
              Agentur-Konditionen anfragen
              <ArrowRight className="size-4 md:size-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <p className="text-xs text-background/60 text-center lg:text-right">
              Antwort innerhalb 24 Stunden · Custom-Pakete möglich
            </p>
          </div>
        </div>
      </div>
    </section>;
}